import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  Alert,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { groupsApi } from '../../../services/groups-api-client';
import { scheduledBroadcastsKey } from '../../../lib/groupQueryKeys';
import { mutationErrorMessage } from '../../../lib/mutation-error';
import { resolveTripTimeZone } from '../../../lib/tripTime';
import { groupScheduledBySequence } from '../../../lib/reminderPlan';
import { foldReplacedPlans } from '../../../lib/reminderPlanNaming';
import {
  scheduledBroadcastConflict,
  scheduledStatusMeta,
  sortScheduledBroadcasts,
} from '../../../lib/scheduledBroadcasts';
import ReminderPlanGroup from './ReminderPlanGroup';
import ScheduledRow from './ScheduledRow';
import { useScheduledBroadcasts } from './useScheduledBroadcasts';
import type { GroupDetail, GroupScheduledBroadcast } from '../../../types/groups';

/**
 * The pending list, mounted below the sent history. Polls itself (via
 * `useScheduledBroadcasts`) while anything is pending/sending, because the
 * background sweep fires due rows with no client action to react to — a host
 * who never touches this screen still needs to see a row flip to "Sent".
 */
export default function ScheduledBroadcastsList({
  group,
  onReuseScheduled,
}: {
  group: GroupDetail;
  /** Loads a reminder that may have been sent (cancelled as in doubt) into the composer. Never sends. */
  onReuseScheduled?: (row: GroupScheduledBroadcast) => void;
}) {
  const queryClient = useQueryClient();
  const { rows, isLoading, unavailable, error } = useScheduledBroadcasts(group.id);
  const { timeZone } = resolveTripTimeZone(group.timeZone);
  const [conflictNote, setConflictNote] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const cancelMutation = useMutation(
    (id: string) => groupsApi.cancelScheduledBroadcast(group.id, id),
    {
      onMutate: (id: string) => setCancellingId(id),
      onSettled: () => setCancellingId(null),
      onSuccess: () => {
        queryClient.invalidateQueries(scheduledBroadcastsKey(group.id));
        setConflictNote(null);
      },
      // A 409 means the row's status moved out from under the host between
      // page-load and click — refresh and show its REAL state, never a
      // generic "that didn't work".
      onError: (err) => {
        const conflict = scheduledBroadcastConflict(err);
        queryClient.invalidateQueries(scheduledBroadcastsKey(group.id));
        setConflictNote(
          conflict
            ? `"${conflict.subject}" is already ${scheduledStatusMeta(conflict.status).label.toLowerCase()}` +
              ' — the list below is refreshed.'
            : mutationErrorMessage(err) || 'Could not cancel that broadcast.',
        );
      },
    },
  );

  // ScheduleControls already renders the designed-state message once; a
  // second copy here would repeat the same sentence directly underneath it.
  if (unavailable) return null;

  // Rows that share a sequenceId (a reminder plan) fold under one header;
  // everything else — every row from an API that predates #394 — is untouched.
  // A closed plan that a reschedule replaced is folded under its replacement, not listed beside it.
  const items = foldReplacedPlans(groupScheduledBySequence(sortScheduledBroadcasts(rows)));

  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>Scheduled sends</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pending first, trip time. A pending send can be cancelled any time before it fires.
      </Typography>
      {conflictNote ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setConflictNote(null)}>{conflictNote}</Alert>
      ) : null}
      {isLoading ? (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={20} /><Typography color="text.secondary">Loading…</Typography>
        </Stack>
      ) : error ? (
        <Alert severity="error">Couldn't load scheduled sends.</Alert>
      ) : items.length === 0 ? (
        <Typography color="text.secondary">Nothing scheduled.</Typography>
      ) : (
        <Stack spacing={1.5}>
          {items.map((item) => (item.kind === 'plan' ? (
            <ReminderPlanGroup
              key={`plan-${item.sequenceId}`}
              group={group}
              rows={item.rows}
              sequenceId={item.sequenceId}
              replaced={item.replaced}
              timeZone={timeZone}
              onCancelRow={(id) => cancelMutation.mutate(id)}
              cancellingId={cancellingId}
              onReuse={onReuseScheduled}
            />
          ) : (
            <ScheduledRow
              key={item.row.id}
              row={item.row}
              timeZone={timeZone}
              onCancel={(id) => cancelMutation.mutate(id)}
              cancelling={cancellingId === item.row.id}
              onReuse={onReuseScheduled}
            />
          )))}
        </Stack>
      )}
    </Paper>
  );
}
